export type MachineStatus = 'customer' | 'stock' | 'rental' | 'sold' | 'out-of-service';

/**
 * Wie diese Maschine beim Weiterverkauf besteuert wird.
 *   standard -- Regelbesteuerung: Umsatzsteuer auf den ganzen Verkaufspreis.
 *   margin   -- Differenzbesteuerung (§ 25a UStG): nur die Marge wird besteuert, die Rechnung
 *               weist keine Steuer aus. Gilt fuer Gegenstaende, die ohne Vorsteuer eingekauft
 *               wurden -- typisch der Ankauf von privat.
 */
export type MachineTaxScheme = 'standard' | 'margin';

export const machineTaxSchemeLabels: Readonly<Record<MachineTaxScheme, string>> = {
  standard: 'Regelbesteuerung',
  margin: 'Differenzbesteuerung § 25a',
};
export type MachineFieldType = 'text' | 'number' | 'boolean' | 'date' | 'select' | 'multiselect';
export type MachineAttributeValue = string | number | boolean | readonly string[] | null;

export interface MachineTypeFieldRecord {
  readonly key: string;
  readonly label: string;
  readonly type: MachineFieldType;
  readonly unit: string;
  readonly required: boolean;
  readonly options: readonly string[];
  readonly sortOrder: number;
  /** Gruppen-/Abschnittsname; steuert, in welchem Workbench-Schritt das Feld erscheint. Leer = "Weitere Angaben". */
  readonly group: string;
}

export interface MachineTypeRecord {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly description: string;
  readonly fields: readonly MachineTypeFieldRecord[];
}

export interface MachineTypeAdminFieldRecord extends MachineTypeFieldRecord {
  readonly id: string;
  readonly active: boolean;
}

export interface MachineTypeAdminRecord extends Omit<MachineTypeRecord, 'fields'> {
  readonly active: boolean;
  readonly machineCount: number;
  readonly fields: readonly MachineTypeAdminFieldRecord[];
}

export interface SaveMachineTypeFieldInput {
  readonly id?: string;
  readonly key: string;
  readonly label: string;
  readonly type: MachineFieldType;
  readonly unit?: string;
  readonly required?: boolean;
  readonly options?: readonly string[];
  readonly sortOrder?: number;
  readonly active?: boolean;
  readonly group?: string;
}

export interface SaveMachineTypeInput {
  readonly tenantId: string;
  readonly machineTypeId?: string;
  readonly code: string;
  readonly name: string;
  readonly description?: string;
  readonly active?: boolean;
  readonly fields: readonly SaveMachineTypeFieldInput[];
}

export interface MachineCustomerOption {
  readonly id: string;
  readonly number: string;
  readonly displayName: string;
  readonly city: string;
}

export interface MachineListRecord {
  readonly id: string;
  readonly number: string;
  readonly typeId: string;
  readonly typeName: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly constructionYear: number | null;
  readonly status: MachineStatus;
  readonly customerId: string | null;
  readonly customerNumber: string;
  readonly customerName: string;
  readonly location: string;
  /** Foto-URL oder Data-URI (leer = Platzhalter). */
  readonly imageUrl: string;
  /** Technische Felder je Maschinentyp (für Kennzahlen in Liste/Karten ohne Detail-Abruf). */
  readonly attributes: Readonly<Record<string, MachineAttributeValue>>;
  /** Einkaufspreis / Ankauf (EK). */
  /**
   * Besteuerungsart beim Weiterverkauf. Entscheidet sich beim ANKAUF und hängt am Gegenstand:
   * Wer von privat oder von einem Kleinunternehmer kauft, hat keine Vorsteuer -- dann wird beim
   * Verkauf nur die Marge besteuert (§ 25a UStG) und die Rechnung weist keine Steuer aus.
   * Fehlt der Wert (Bestandsdaten), gilt Regelbesteuerung.
   */
  readonly taxScheme?: MachineTaxScheme;
  readonly purchasePrice: number;
  /** Verkaufspreis (0 = nicht zum Verkauf). */
  readonly salesPrice: number;
  /** Miet-Standardsätze (0 = nicht angeboten); werden im Mietauftrag vorbelegt. */
  readonly rentalDailyRate: number;
  readonly rentalWeeklyRate: number;
  readonly rentalMonthlyRate: number;
  readonly rentalMinimumDays: number;
  /** Zugang: seit wann im Bestand (ISO-Datum). */
  readonly activeSince: string;
  /** Abgang: seit wann nicht mehr im Bestand oder null (noch im Bestand). */
  readonly activeUntil: string | null;
  /** Warengruppe je Maschine (Stufe 2, Feinzuordnung) — optional. */
  readonly warengruppeId?: string | null;
  readonly warengruppeName?: string;
  /** Nächste UVV-Prüfung fällig (ISO-Datum) oder null (keine hinterlegt). */
  readonly nextUvvDate?: string | null;
  /** Letzte durchgeführte UVV-Prüfung (ISO-Datum) oder null. */
  readonly lastUvvDate?: string | null;
  readonly updatedAt: string;
}

export interface MachineDetailRecord extends MachineListRecord {
  readonly tenantId: string;
  readonly notes: string;
  readonly createdAt: string;
}

export interface MachineSearchInput {
  readonly tenantId: string;
  readonly query?: string;
  readonly status?: MachineStatus | 'all';
  readonly typeId?: string | 'all';
  /** Restrict to machines assigned to this customer (business number). */
  readonly customerNumber?: string;
  /** Bestand-im-Zeitraum: nur Maschinen, die zwischen diesen Daten im Bestand waren. */
  readonly bestandFrom?: string;
  readonly bestandTo?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedMachineSearchInput {
  readonly tenantId: string;
  readonly query: string;
  readonly status: MachineStatus | 'all';
  readonly typeId: string | 'all';
  readonly customerNumber: string;
  readonly bestandFrom: string;
  readonly bestandTo: string;
  readonly page: number;
  readonly pageSize: number;
}

export interface MachinePage {
  readonly items: readonly MachineListRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface CreateMachineInput {
  readonly tenantId: string;
  readonly typeId: string;
  readonly customerId?: string | null;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber?: string;
  readonly constructionYear?: number | null;
  readonly status?: MachineStatus;
  readonly location?: string;
  readonly imageUrl?: string;
  readonly purchasePrice?: number;
  readonly salesPrice?: number;
  readonly rentalDailyRate?: number;
  readonly rentalWeeklyRate?: number;
  readonly rentalMonthlyRate?: number;
  readonly rentalMinimumDays?: number;
  readonly activeSince?: string;
  readonly notes?: string;
  readonly warengruppeId?: string | null;
  readonly attributes?: Readonly<Record<string, unknown>>;
}

export interface UpdateMachineInput extends Omit<CreateMachineInput, 'tenantId'> {
  readonly tenantId: string;
  readonly machineId: string;
}

/** Ein mit der Maschine verknüpfter Vorgang (Miete, Service, Auftrag) -- für den Vorgänge-Tab. */
export interface MachineOperation {
  readonly kind: 'rental' | 'service' | 'order';
  readonly number: string;
  readonly title: string;
  readonly status: string;
  readonly date: string;
}

export type MachineAttachmentKind = 'image' | 'document';

/** Ein Anhang der Maschinenakte (Bild oder Dokument). content ist eine data:-URL; in Listen ist er
 * bei Dokumenten leer und wird beim Download einzeln nachgeladen. */
export interface MachineAttachmentRecord {
  readonly id: string;
  readonly kind: MachineAttachmentKind;
  readonly title: string;
  readonly mimeType: string;
  readonly byteSize: number;
  readonly createdAt: string;
  readonly content: string;
}

export interface AddMachineAttachmentInput {
  readonly tenantId: string;
  readonly machineId: string;
  readonly kind: MachineAttachmentKind;
  readonly title: string;
  readonly mimeType: string;
  readonly byteSize: number;
  readonly content: string;
}

export interface MachinesGateway {
  listMachineTypes(tenantId: string): Promise<readonly MachineTypeRecord[]>;
  listMachineTypesForAdmin(tenantId: string): Promise<readonly MachineTypeAdminRecord[]>;
  saveMachineType(input: SaveMachineTypeInput): Promise<readonly MachineTypeAdminRecord[]>;
  searchCustomerOptions(tenantId: string, query?: string): Promise<readonly MachineCustomerOption[]>;
  searchMachines(input: MachineSearchInput): Promise<MachinePage>;
  getMachine(tenantId: string, machineId: string): Promise<MachineDetailRecord>;
  createMachine(input: CreateMachineInput): Promise<MachineDetailRecord>;
  updateMachine(input: UpdateMachineInput): Promise<MachineDetailRecord>;
  /** Maschinenakte: Anhänge (Bilder + Dokumente) und Bemerkungen. */
  listAttachments(tenantId: string, machineId: string): Promise<readonly MachineAttachmentRecord[]>;
  getAttachment(tenantId: string, attachmentId: string): Promise<MachineAttachmentRecord>;
  addAttachment(input: AddMachineAttachmentInput): Promise<MachineAttachmentRecord>;
  removeAttachment(tenantId: string, attachmentId: string): Promise<void>;
  updateMachineNotes(tenantId: string, machineId: string, notes: string): Promise<MachineDetailRecord>;
  /**
   * Besteuerungsart setzen (§ 25a). Eigene schmale Methode statt eines Feldes in updateMachine:
   * die Entscheidung faellt beim Ankauf, haengt am Einkaufspreis und ist kaufmaennisch -- sie
   * gehoert nicht in dieselbe Maske wie Hersteller und Seriennummer.
   */
  setMachineTaxScheme(tenantId: string, machineId: string, scheme: MachineTaxScheme): Promise<MachineDetailRecord>;
  /** UVV-Prüftermine setzen (nächste + letzte Prüfung); schmaler Schreibpfad, überschreibt nur diese Felder. */
  setMachineInspection(
    tenantId: string,
    machineId: string,
    nextUvvDate: string | null,
    lastUvvDate: string | null,
  ): Promise<MachineDetailRecord>;
}

export function normalizeMachineSearchInput(input: MachineSearchInput): NormalizedMachineSearchInput {
  return {
    tenantId: input.tenantId,
    query: input.query?.trim() ?? '',
    status: input.status ?? 'all',
    typeId: input.typeId ?? 'all',
    customerNumber: input.customerNumber?.trim() ?? '',
    bestandFrom: input.bestandFrom?.trim() ?? '',
    bestandTo: input.bestandTo?.trim() ?? '',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

const fieldTypes = new Set<MachineFieldType>(['text', 'number', 'boolean', 'date', 'select', 'multiselect']);
/** Feldtypen, die eine Auswahlliste (options) brauchen: Einfach- und Mehrfachauswahl. */
function needsOptions(type: MachineFieldType): boolean { return type === 'select' || type === 'multiselect'; }

export function normalizeMachineTypeInput(input: SaveMachineTypeInput): SaveMachineTypeInput {
  const code = input.code.trim().toLocaleLowerCase('de');
  const name = input.name.trim();
  if (!/^[a-z0-9_-]+$/.test(code)) {
    throw new Error('Der Typ-Code darf nur Kleinbuchstaben, Zahlen, Bindestriche und Unterstriche enthalten.');
  }
  if (!name) throw new Error('Der Maschinentyp benötigt einen Namen.');
  if (input.fields.length > 50) throw new Error('Ein Maschinentyp darf höchstens 50 dynamische Felder enthalten.');

  const seenKeys = new Set<string>();
  const fields = input.fields.map((field, index): SaveMachineTypeFieldInput => {
    const key = field.key.trim();
    const label = field.label.trim();
    if (!/^[a-zA-Z][a-zA-Z0-9_-]*$/.test(key)) {
      throw new Error(`Der Feldschlüssel ${key || '(leer)'} ist ungültig.`);
    }
    if (seenKeys.has(key)) throw new Error(`Der Feldschlüssel ${key} ist doppelt vorhanden.`);
    seenKeys.add(key);
    if (!label) throw new Error(`Das Feld ${key} benötigt eine Bezeichnung.`);
    if (!fieldTypes.has(field.type)) throw new Error(`Das Feld ${key} besitzt einen ungültigen Datentyp.`);

    const options = [...new Set((field.options ?? []).map((option) => option.trim()).filter(Boolean))];
    if (needsOptions(field.type) && options.length === 0) {
      throw new Error(`${label} benötigt mindestens einen Auswahlwert.`);
    }

    return {
      ...(field.id ? { id: field.id } : {}),
      key,
      label,
      type: field.type,
      unit: field.unit?.trim() ?? '',
      required: Boolean(field.required),
      options: needsOptions(field.type) ? options : [],
      sortOrder: (index + 1) * 10,
      active: field.active ?? true,
      group: field.group?.trim() ?? '',
    };
  });

  return {
    tenantId: input.tenantId,
    ...(input.machineTypeId ? { machineTypeId: input.machineTypeId } : {}),
    code,
    name,
    description: input.description?.trim() ?? '',
    active: input.active ?? true,
    fields,
  };
}

function isEmpty(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

export function validateMachineAttributes(
  machineType: MachineTypeRecord,
  attributes: Readonly<Record<string, unknown>> = {},
): Readonly<Record<string, MachineAttributeValue>> {
  const allowed = new Set(machineType.fields.map((field) => field.key));
  const unknownKey = Object.keys(attributes).find((key) => !allowed.has(key));
  if (unknownKey) throw new Error(`Das dynamische Feld ${unknownKey} gehört nicht zum Maschinentyp.`);

  const normalized: Record<string, MachineAttributeValue> = {};
  for (const field of [...machineType.fields].sort((left, right) => left.sortOrder - right.sortOrder)) {
    const raw = attributes[field.key];
    if (isEmpty(raw)) {
      // Bei Maschinen gibt es beim Anlegen KEINE Pflicht-Attribute (bewusste Entscheidung): ein leeres
      // Feld wird als "nicht gesetzt" gespeichert und blockiert nie. Fehlende Technikdaten trägt man
      // später in der Geräteakte nach. `field.required` bleibt am Feld nur als Anzeige-Hinweis.
      normalized[field.key] = null;
      continue;
    }

    if (field.type === 'number') {
      const value = typeof raw === 'number' ? raw : Number(raw);
      if (!Number.isFinite(value)) throw new Error(`${field.label} muss eine Zahl sein.`);
      normalized[field.key] = value;
      continue;
    }

    if (field.type === 'boolean') {
      if (typeof raw !== 'boolean') throw new Error(`${field.label} muss Ja oder Nein sein.`);
      normalized[field.key] = raw;
      continue;
    }

    if (field.type === 'multiselect') {
      const list = Array.isArray(raw) ? raw : [];
      const values = [...new Set(list.map((item) => String(item).trim()).filter((item) => field.options.includes(item)))];
      normalized[field.key] = values;
      continue;
    }

    if (typeof raw !== 'string') throw new Error(`${field.label} muss als Text angegeben werden.`);
    const value = raw.trim();
    if (field.type === 'select' && !field.options.includes(value)) {
      throw new Error(`${field.label} enthält einen nicht erlaubten Wert.`);
    }
    normalized[field.key] = value;
  }
  return normalized;
}
